import * as React from 'react';

import type {
    BodyParameters,
    CallbackAnyParam,
    FeedbackMessage,
    FetchRequest,
    QueryParameters,
    CallbackNoParams,
    CallbackStringParam,
} from "./GeneralInterfaces";
import {FetchControl} from "../utils/FetchControl";
import {
    ApiEndPoints,
    FeedbackMessageTypes,
    HttpMethods
} from "../Settings";


/**
 * This interface defines the properties of the class [[`BaseComponent`]]. All classes deriving from this class must
 * provide properties that extend this interface.
 */
export interface BaseComponentProps {
    /** Callback function to trigger the display of user feedback messages on screen. */
    displayMessage: (feedback_message: FeedbackMessage) => void;
}


/**
 * This interface defines the state of the class [[`BaseComponent`]]. All classes deriving from this class must
 * use a state interface that derives from this interface.
 */
export interface BaseComponentState {
    /** Indicates whether there are pending fetch requests */
    pending_fetch_requests: boolean;
}


/**
 * This constant holds the initialization for all state attributes of the class [[`BaseComponent`]]. It can be used in
 * the child classes for initializing the state.
 */
export const BaseComponentStateInitial: BaseComponentState = {
    /** Initialization for pending fetch request indicator */
    pending_fetch_requests: false,
}


/**
 * This class is the base class for all React components in the frontend that need to communicate with the server. Its
 * main purpose is to provide infrastructure for the client-server communication as well as for displaying user
 * feedback messages.
 */
export class BaseComponent<PropsInterface extends BaseComponentProps, StateInterface extends BaseComponentState>
    extends React.Component<PropsInterface, StateInterface> {

    /**
     * Holds the local fetch controller of this React component.
     * @private
     */
    private readonly fetch_control: FetchControl;


    /**
     * Constructor. Initializes the local fetch controller.
     * @param props React properties passed from the calling instance.
     */
    constructor(props: PropsInterface) {
        super(props);

        this.fetch_control = new FetchControl();
    }


    /**
     * This function is called by React automatically directly before the component is unmounted from the DOM tree.
     * It cancels all pending fetch requests as their responses will no longer be processed due to the removal of this
     * component.
     */
    componentWillUnmount() {
        this.fetch_control.cancelAllPendingFetchRequests();
    }


    /**
     * Getter function for providing the base component's fetch controller.
     * @returns The fetch controller of this component.
     */
    getFetchControl(): FetchControl {
        return this.fetch_control;
    }


    /**
     * Sets the state variable pending_fetch_requests according to whether or not there are currently pending fetch
     * requests.
     */
    updatePendingFetchRequestState() {
        this.setState({pending_fetch_requests: this.fetch_control.getNumberPendingFetchRequests() > 0})
    }


    /**
     * Convenience function for issuing an error message that is to be displayed on screen.
     * @param message Message text that is to be displayed on screen.
     */
    displayError(message: string) {
        const feedback_message: FeedbackMessage = {
            message_type: FeedbackMessageTypes.ERROR,
            message_text: message
        }
        this.props.displayMessage(feedback_message);
    }


    /**
     * Convenience function for issuing a warning message that is to be displayed on screen.
     * @param message Message text that is to be displayed on screen.
     */
    displayWarning(message: string) {
        const feedback_message: FeedbackMessage = {
            message_type: FeedbackMessageTypes.WARNING,
            message_text: message
        }
        this.props.displayMessage(feedback_message);
    }


    /**
     * Convenience function for issuing a user info message that is to be displayed on screen.
     * @param message Message text that is to be displayed on screen.
     */
    displayInfo(message: string) {
        const feedback_message: FeedbackMessage = {
            message_type: FeedbackMessageTypes.INFO,
            message_text: message
        }
        this.props.displayMessage(feedback_message);
    }


    /**
     * Convenience function for issuing a user success message that is to be displayed on screen.
     * @param message Message text that is to be displayed on screen.
     */
    displaySuccess(message: string) {
        const feedback_message: FeedbackMessage = {
            message_type: FeedbackMessageTypes.SUCCESS,
            message_text: message
        }
        this.props.displayMessage(feedback_message);
    }


    /**
     * This routine cancels all pending fetch promises in the promise cache of the local fetch controller.
     */
    cancelAllCachedPromises() {
        this.fetch_control.cancelAllPendingFetchRequests();
    }

    /**
     * Convenience function for calling requestFetchPromise of the local fetch controller.
     * @param api_endpoint End-point address of rest API, for instance: /api/login
     * @param query_parameters Optional query-string parameters to be included in request URL, for instance:
     * {model_id: 1}
     * rollback point is added to the query string. This is a convenience functionality. Alternatively, the rollback
     * point can also be provided directly in the query_parameters parameter. If not specified or undefined,
     * no rollback point is not added to the query string.
     * @param method HTTP request method to be used in restful API call, for instance: "GET"
     * @param body Optional parameters to be included in the request body of API call, for instance: {selected: true}
     * @param file Optional file to be uploaded along with the restful API call.
     * @returns Returns a Promise object that can be used to process the server response after it has been received.
     * */
    requestFetchPromise(api_endpoint: ApiEndPoints,
                        method: HttpMethods,
                        query_parameters: QueryParameters,
                        body?: BodyParameters,
                        file?: File): Promise<any> {
        return this.fetch_control.requestFetchPromise(
            api_endpoint,
            method,
            query_parameters,
            body,
            file)
    }


    /**
     * Convenience function for calling requestFetchPromise adding, however, a processing callback that is called
     * after successfully receiving the server response for handling its processing. Also, exceptions are automatically
     * passed to the component's displayMessage function. The parameters necessary for triggering the api-call through
     * the routine requestFetchPromise are conveniently embedded here in a FetchRequest object passed as parameter.
     *
     * @param fetch_request Holds the parameters necessary for triggering the api call
     * @param processing_callback Callback for processing the successful server response
     */
    processFetchRequest(fetch_request: FetchRequest, processing_callback: CallbackAnyParam) {
        this.requestFetchPromise(
            fetch_request.api_endpoint,
            fetch_request.method,
            fetch_request.query_parameters,
            fetch_request.body,
            fetch_request.file
        ).then(value => {
            this.updatePendingFetchRequestState();
            processing_callback(value)
        }).catch(feedback_message => {
            this.updatePendingFetchRequestState();
            this.props.displayMessage(feedback_message);
        })
        this.updatePendingFetchRequestState();
    }


    //TODO: Double check this code
    /**
     * Convenience function for calling requestFetchStreamPromise of the local fetch controller.
     * Delivers text chunks to the provided callback and routes errors via displayMessage.
     * @param api_endpoint End-point address of rest API (e.g., /api/chat)
     * @param method HTTP request method to be used (typically POST)
     * @param query_parameters Query parameters to be included in the request URL
     * @param onChunk Callback invoked for each decoded text chunk
     * @param body Optional parameters to be included in the request body of API call
     */
    requestFetchStreamPromise(api_endpoint: ApiEndPoints,
                              method: HttpMethods,
                              query_parameters: QueryParameters,
                              onChunk: CallbackStringParam,
                              body?: BodyParameters): Promise<void> {
        return this.fetch_control.requestFetchStreamPromise(
            api_endpoint,
            method,
            query_parameters,
            onChunk,
            body,
        ).then(() => {
            this.updatePendingFetchRequestState();
        }).catch((feedback_message: FeedbackMessage) => {
            this.updatePendingFetchRequestState();
            this.props.displayMessage(feedback_message);
            throw feedback_message;
        });
    }


    //TODO: Double check this code
    /**
     * Convenience function similar to processFetchRequest for streaming endpoints.
     * Starts a streaming request and forwards chunks to the processing callback.
     * Errors are automatically passed to displayMessage via requestFetchStreamPromise.
     * @param fetch_request Holds the parameters necessary for triggering the streaming api call
     * @param processing_chunk_callback Callback for processing each received text chunk
     * @param finally_callback Optional callback invoked when the stream completes or errors (equivalent to Promise.finally)
     */
    processFetchStreamRequest(fetch_request: FetchRequest,
                              processing_chunk_callback: CallbackStringParam,
                              finally_callback?: CallbackNoParams): void {
        // Set pending state immediately similar to processFetchRequest
        this.updatePendingFetchRequestState();
        const p = this.requestFetchStreamPromise(
            fetch_request.api_endpoint,
            fetch_request.method,
            fetch_request.query_parameters,
            processing_chunk_callback,
            fetch_request.body,
        );
        if (finally_callback) p.finally(finally_callback);
    }
}
